import React, { useState } from "react";
import { useGetReturnsQuery, useGetSalesQuery, useGetSaleQuery, useCreateReturnMutation } from "../services/api";
import { getApiV1Url } from "../services/apiConfig";
import { FiRotateCcw, FiPlus } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", { month: "short", day: "numeric", year: "numeric" });
}

export default function ReturnsPage() {
  const { data, isLoading } = useGetReturnsQuery();
  const { data: salesData } = useGetSalesQuery();
  const [createReturn] = useCreateReturnMutation();
  
  const returns = data?.data || [];
  const sales = salesData?.data || [];

  // Calculate KPIs
  const totalReturns = returns.length;
  const totalRefunded = returns.reduce((sum, ret) => sum + Number(ret.refund_amount), 0);
  const completedReturns = returns.filter(r => r.status === "completed").length;
  const pendingReturns = returns.filter(r => r.status !== "completed").length;

  const [showModal, setShowModal] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState("");
  const [saleDetails, setSaleDetails] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [form, setForm] = useState({
    reason: "refund",
    refund_method: "cash",
    notes: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSaleSelect = async (saleId) => {
    setSelectedSaleId(saleId);
    if (!saleId) {
      setSaleDetails(null);
      setSelectedItems([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${getApiV1Url()}/sales/${saleId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const result = await response.json();
      if (result.success) {
        setSaleDetails(result.data);
        // Pre-select all items with quantity 1
        setSelectedItems(result.data.items.map(item => ({
          sale_item_id: item.id,
          product_id: item.product_id,
          product_name: item.product_name,
          weight_grams: item.weight_grams,
          quantity: 1,
          unit_price: item.unit_price,
          stock_item_id: null, // Will need to be set if stock restoration is needed
        })));
      }
    } catch (err) {
      setError("Failed to load sale details");
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (index) => {
    const newItems = [...selectedItems];
    if (newItems[index].quantity > 0) {
      newItems[index].quantity = 0;
    } else {
      newItems[index].quantity = 1;
    }
    setSelectedItems(newItems);
  };

  const updateQuantity = (index, quantity) => {
    const newItems = [...selectedItems];
    const maxQty = saleDetails.items[index].quantity;
    newItems[index].quantity = Math.min(Math.max(0, Number(quantity)), maxQty);
    setSelectedItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const itemsToReturn = selectedItems.filter(item => item.quantity > 0);
    if (itemsToReturn.length === 0) {
      setError("Please select at least one item to return");
      return;
    }

    try {
      await createReturn({
        original_sale_id: selectedSaleId,
        items: itemsToReturn,
        reason: form.reason,
        refund_method: form.refund_method,
        notes: form.notes,
      }).unwrap();
      
      setShowModal(false);
      setSelectedSaleId("");
      setSaleDetails(null);
      setSelectedItems([]);
      setForm({ reason: "refund", refund_method: "cash", notes: "" });
    } catch (err) {
      setError(err?.data?.message || "Failed to process return");
    }
  };

  const openModal = () => {
    setShowModal(true);
    setSelectedSaleId("");
    setSaleDetails(null);
    setSelectedItems([]);
    setForm({ reason: "refund", refund_method: "cash", notes: "" });
    setError("");
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "220px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minMax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Returns & Exchanges
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Process product returns and exchanges
          </p>
        </div>
        <button 
          onClick={openModal}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
          }}
        >
          <FiPlus /> Process Return
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Returns */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🔄
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Returns
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalReturns}
          </div>
        </div>

        {/* Total Refunded */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💸
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Refunded
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(totalRefunded)}
          </div>
        </div>

        {/* Completed */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ✓
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Completed
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {completedReturns}
          </div>
        </div>

        {/* Pending */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ⏳
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Pending
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {pendingReturns}
          </div>
        </div>
      </div>

      {/* Returns Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          Return History
        </h3>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Return #</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Date</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Original Sale</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Customer</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Reason</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Refund Amount</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Method</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {returns.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                  <FiRotateCcw size={48} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
                  <div>No returns yet</div>
                </td>
              </tr>
            ) : (
              returns.map((ret) => (
                <tr key={ret.id} style={{ 
                  borderBottom: "1px solid #F1F5F9",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
                >
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>{ret.return_number}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatDate(ret.created_at)}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{ret.original_sale_number}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{ret.customer_name || "Walk-in"}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: "#FEF3C7",
                      color: "#92400E"
                    }}>
                      {ret.reason}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 700, color: "#EF4444" }}>{formatCurrency(ret.refund_amount)}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{ret.refund_method}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: ret.status === "completed" ? "#D1FAE5" : "#FEF3C7",
                      color: ret.status === "completed" ? "#065F46" : "#92400E"
                    }}>
                      {ret.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "800px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Process Return</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Original Sale *</label>
                  <select 
                    className="form-select" 
                    value={selectedSaleId} 
                    onChange={(e) => handleSaleSelect(e.target.value)} 
                    required
                  >
                    <option value="">-- Select the sale to return --</option>
                    {sales.map((s) => (
                      <option key={s.id} value={s.id}>
                        Sale #{s.sale_number} - {formatCurrency(s.total_amount)} - {formatDate(s.created_at)} - {s.customer_name || "Walk-in"}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    Choose the original sale that contains items to return
                  </div>
                </div>

                {loading && <div style={{ textAlign: "center", padding: "var(--space-4)" }}>Loading sale details...</div>}

                {saleDetails && (
                  <>
                    <div style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-3)" }}>
                      <h4 style={{ fontSize: "1rem", marginBottom: "var(--space-2)" }}>Select Items to Return:</h4>
                      <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--border-radius-sm)", overflow: "hidden" }}>
                        {saleDetails.items.map((item, index) => (
                          <div 
                            key={item.id} 
                            style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              padding: "var(--space-3)", 
                              borderBottom: index < saleDetails.items.length - 1 ? "1px solid var(--color-border)" : "none",
                              background: selectedItems[index]?.quantity > 0 ? "var(--color-surface-elevated)" : "transparent"
                            }}
                          >
                            <input 
                              type="checkbox" 
                              checked={selectedItems[index]?.quantity > 0}
                              onChange={() => toggleItem(index)}
                              style={{ marginRight: "var(--space-3)" }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>{item.product_name}</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)" }}>
                                {formatCurrency(item.unit_price)} × {item.quantity} = {formatCurrency(item.line_total)}
                              </div>
                            </div>
                            {selectedItems[index]?.quantity > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                                <label style={{ fontSize: "0.875rem" }}>Qty:</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max={item.quantity}
                                  value={selectedItems[index]?.quantity || 1}
                                  onChange={(e) => updateQuantity(index, e.target.value)}
                                  style={{ width: "80px" }}
                                  className="form-input"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ 
                      padding: "var(--space-3)", 
                      background: "var(--color-surface-elevated)", 
                      borderRadius: "var(--border-radius-sm)",
                      marginBottom: "var(--space-4)"
                    }}>
                      <strong>Refund Amount: </strong>
                      {formatCurrency(selectedItems.reduce((sum, item, idx) => 
                        sum + (item.quantity * saleDetails.items[idx].unit_price), 0
                      ))}
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">Return Reason *</label>
                  <select className="form-select" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                    <option value="refund">Customer Refund Request</option>
                    <option value="exchange">Exchange for Different Item</option>
                    <option value="defect">Product Defect/Damage</option>
                    <option value="other">Other Reason</option>
                  </select>
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    Why is this item being returned?
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Refund Method *</label>
                  <select className="form-select" value={form.refund_method} onChange={(e) => setForm({ ...form, refund_method: e.target.value })}>
                    <option value="cash">Cash Refund</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="store_credit">Store Credit</option>
                  </select>
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    How will the customer receive their refund?
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Additional Notes (Optional)</label>
                  <textarea 
                    className="form-textarea" 
                    value={form.notes} 
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} 
                    placeholder="e.g., Customer not satisfied with design, Item had scratches, etc."
                    rows="3" 
                  />
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    Any additional information about this return
                  </div>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={!saleDetails || loading}>Process Return</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
